import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { getToken } from '../../services/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Video,
  Plus,
  Play,
  Square,
  Trash2,
  Users,
  Calendar,
  Clock,
  Eye,
  ExternalLink,
  Settings,
  Youtube,
  Radio,
  CheckCircle2,
  AlertCircle,
  Copy,
  Loader2,
  UserPlus,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLives = () => {
  const [searchParams] = useSearchParams();
  const [liveStreams, setLiveStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [youtubeStatus, setYoutubeStatus] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSpeakersModal, setShowSpeakersModal] = useState(false);
  const [selectedStream, setSelectedStream] = useState(null);
  const [availableSpeakers, setAvailableSpeakers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    scheduled_start_time: '',
    scheduled_end_time: '',
    privacy_status: 'unlisted',
    category: 'masterclass',
    access_level: 'affiliate',
    speaker_ids: []
  });

  useEffect(() => {
    loadData();
    
    // Check for YouTube connection callback
    const ytConnected = searchParams.get('youtube_connected');
    const ytError = searchParams.get('youtube_error');
    
    if (ytConnected === 'true') {
      alert('¡YouTube conectado exitosamente!');
    }
    if (ytError) {
      alert(`Error al conectar YouTube: ${ytError}`);
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      // Load YouTube status
      const statusRes = await fetch(`${API_URL}/api/youtube/status`, { headers });
      const statusData = await statusRes.json();
      setYoutubeStatus(statusData);
      
      // Load live streams
      const streamsRes = await fetch(`${API_URL}/api/youtube/lives?limit=50`, { headers });
      const streamsData = await streamsRes.json();
      setLiveStreams(streamsData.live_streams || []);
      
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const connectYouTube = () => {
    const token = getToken();
    if (!token) {
      alert('Debes iniciar sesión primero');
      window.location.href = '/login';
      return;
    }
    
    // Redirect directly to the auth endpoint with token
    window.location.href = `${API_URL}/api/youtube/auth?token=${encodeURIComponent(token)}`;
  };

  const loadAvailableSpeakers = async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/youtube/speakers/available?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setAvailableSpeakers(data.speakers || []);
    } catch (error) {
      console.error('Error loading speakers:', error);
    }
  };

  const createLiveStream = async () => {
    if (!formData.title || !formData.scheduled_start_time) {
      alert('Por favor completa los campos requeridos');
      return;
    }
    
    setCreating(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/youtube/lives`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          scheduled_start_time: new Date(formData.scheduled_start_time).toISOString(),
          scheduled_end_time: formData.scheduled_end_time ? new Date(formData.scheduled_end_time).toISOString() : null
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        alert(`¡Transmisión creada!\n\nURL de transmisión: ${data.rtmp_url}\nClave: ${data.stream_key}\n\nUsa estos datos en OBS o tu software de streaming.`);
        setShowCreateModal(false);
        setFormData({
          title: '',
          description: '',
          scheduled_start_time: '',
          scheduled_end_time: '',
          privacy_status: 'unlisted',
          category: 'masterclass',
          access_level: 'affiliate',
          speaker_ids: []
        });
        loadData();
      } else {
        alert(data.detail || 'Error al crear transmisión');
      }
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setCreating(false);
    }
  };

  const startStream = async (streamId) => {
    if (!confirm('¿Iniciar transmisión en vivo?')) return;
    
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/youtube/lives/${streamId}/start`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await res.json();
      if (res.ok) {
        alert('¡Transmisión iniciada!');
        loadData();
      } else {
        alert(data.detail || 'Error al iniciar');
      }
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const endStream = async (streamId) => {
    if (!confirm('¿Finalizar transmisión? La grabación se guardará en YouTube.')) return;
    
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/youtube/lives/${streamId}/end`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await res.json();
      if (res.ok) {
        alert('Transmisión finalizada. Grabación guardada.');
        loadData();
      } else {
        alert(data.detail || 'Error al finalizar');
      }
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const deleteStream = async (streamId) => {
    if (!confirm('¿Eliminar esta transmisión?')) return;
    
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/youtube/lives/${streamId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        loadData();
      } else {
        const data = await res.json();
        alert(data.detail || 'Error al eliminar');
      }
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copiado al portapapeles');
  };

  const getStatusBadge = (status) => {
    const styles = {
      scheduled: 'bg-blue-100 text-blue-700',
      live: 'bg-red-100 text-red-700 animate-pulse',
      completed: 'bg-green-100 text-green-700',
      cancelled: 'bg-gray-100 text-gray-700'
    };
    const labels = {
      scheduled: 'Programado',
      live: 'EN VIVO',
      completed: 'Finalizado',
      cancelled: 'Cancelado'
    };
    return <Badge className={styles[status] || styles.scheduled}>{labels[status] || status}</Badge>;
  };

  const getCategoryLabel = (cat) => {
    const labels = {
      masterclass: 'Master Class',
      webinar: 'Webinar',
      conferencia: 'Conferencia',
      tutorial: 'Tutorial'
    };
    return labels[cat] || cat;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Radio className="h-6 w-6 text-red-500" />
              Transmisiones en Vivo
            </h1>
            <p className="text-gray-500">Gestiona las transmisiones de YouTube Live para Master Classes</p>
          </div>
          
          <div className="flex gap-3">
            {youtubeStatus?.user_authenticated ? (
              <Button onClick={() => { loadAvailableSpeakers(); setShowCreateModal(true); }} className="bg-red-600 hover:bg-red-700">
                <Plus className="h-4 w-4 mr-2" />
                Nueva Transmisión
              </Button>
            ) : (
              <Button onClick={connectYouTube} className="bg-red-600 hover:bg-red-700">
                <Youtube className="h-4 w-4 mr-2" />
                Conectar YouTube
              </Button>
            )}
          </div>
        </div>

        {/* YouTube Status Card */}
        <Card className="border-2 border-dashed">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-full ${youtubeStatus?.user_authenticated ? 'bg-green-100' : 'bg-yellow-100'}`}>
                  <Youtube className={`h-6 w-6 ${youtubeStatus?.user_authenticated ? 'text-green-600' : 'text-yellow-600'}`} />
                </div>
                <div>
                  <h3 className="font-semibold">Estado de YouTube</h3>
                  <p className="text-sm text-gray-500">
                    {youtubeStatus?.user_authenticated ? (
                      <span className="flex items-center gap-1 text-green-600">
                        <CheckCircle2 className="h-4 w-4" />
                        Cuenta conectada
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-yellow-600">
                        <AlertCircle className="h-4 w-4" />
                        {youtubeStatus?.configured ? 'No conectado - Haz clic en "Conectar YouTube"' : 'Falta configuración del servidor'}
                      </span>
                    )}
                  </p>
                </div>
              </div>
              
              {!youtubeStatus?.configured && youtubeStatus?.missing_credentials?.length > 0 && (
                <div className="text-sm text-red-500">
                  Falta: {youtubeStatus.missing_credentials.join(', ')}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Live Streams Grid */}
        {liveStreams.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Video className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No hay transmisiones</h3>
              <p className="text-gray-500 mb-4">Crea tu primera transmisión en vivo para Master Classes</p>
              {youtubeStatus?.user_authenticated && (
                <Button onClick={() => { loadAvailableSpeakers(); setShowCreateModal(true); }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Crear Transmisión
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {liveStreams.map((stream) => (
              <Card key={stream.id} className={`overflow-hidden ${stream.status === 'live' ? 'ring-2 ring-red-500' : ''}`}>
                <div className="aspect-video bg-gray-100 relative">
                  {stream.status === 'live' && stream.embed_url ? (
                    <iframe
                      src={stream.embed_url}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; encrypted-media"
                      allowFullScreen
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                      <Video className="h-12 w-12 text-gray-500" />
                    </div>
                  )}
                  <div className="absolute top-2 left-2">
                    {getStatusBadge(stream.status)}
                  </div>
                  <div className="absolute top-2 right-2">
                    <Badge variant="secondary">{getCategoryLabel(stream.category)}</Badge>
                  </div>
                </div>
                
                <CardContent className="p-4">
                  <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">{stream.title}</h3>
                  
                  <div className="space-y-2 text-sm text-gray-500 mb-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      {new Date(stream.scheduled_start_time).toLocaleDateString('es-MX', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      {new Date(stream.scheduled_start_time).toLocaleTimeString('es-MX', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })} hrs
                    </div>
                    {stream.speakers?.length > 0 && (
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        {stream.speakers.length} ponente(s)
                      </div>
                    )}
                    {stream.viewer_count > 0 && (
                      <div className="flex items-center gap-2">
                        <Eye className="h-4 w-4" />
                        {stream.viewer_count} espectadores
                      </div>
                    )}
                  </div>
                  
                  {/* Stream credentials for admin */}
                  {stream.rtmp_url && stream.status === 'scheduled' && (
                    <div className="bg-gray-50 rounded-lg p-3 mb-4 text-xs">
                      <p className="font-medium mb-1">Datos de transmisión:</p>
                      <div className="flex items-center gap-1 mb-1">
                        <span className="text-gray-500">RTMP:</span>
                        <code className="flex-1 truncate">{stream.rtmp_url}</code>
                        <button onClick={() => copyToClipboard(stream.rtmp_url)} className="text-blue-500 hover:text-blue-700">
                          <Copy className="h-3 w-3" />
                        </button>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-gray-500">Clave:</span>
                        <code className="flex-1 truncate">{stream.stream_key}</code>
                        <button onClick={() => copyToClipboard(stream.stream_key)} className="text-blue-500 hover:text-blue-700">
                          <Copy className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="flex gap-2">
                    {stream.status === 'scheduled' && (
                      <>
                        <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => startStream(stream.id)}>
                          <Play className="h-4 w-4 mr-1" />
                          Iniciar
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setSelectedStream(stream); loadAvailableSpeakers(); setShowSpeakersModal(true); }}>
                          <UserPlus className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteStream(stream.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                    
                    {stream.status === 'live' && (
                      <>
                        <Button size="sm" className="flex-1 bg-red-600 hover:bg-red-700" onClick={() => endStream(stream.id)}>
                          <Square className="h-4 w-4 mr-1" />
                          Finalizar
                        </Button>
                        {stream.watch_url && (
                          <Button size="sm" variant="outline" asChild>
                            <a href={stream.watch_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </Button>
                        )}
                      </>
                    )}
                    
                    {stream.status === 'completed' && stream.recording_url && (
                      <Button size="sm" variant="outline" className="flex-1" asChild>
                        <a href={stream.recording_url} target="_blank" rel="noopener noreferrer">
                          <Video className="h-4 w-4 mr-1" />
                          Ver Grabación
                        </a>
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Radio className="h-5 w-5 text-red-500" />
                Nueva Transmisión en Vivo
              </DialogTitle>
              <DialogDescription>
                Programa una Master Class en vivo que se transmitirá por YouTube
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label>Título *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Ej: Master Class de Ventas Inmobiliarias"
                />
              </div>
              
              <div>
                <Label>Descripción</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe el contenido de la transmisión..."
                  rows={3}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fecha y Hora de Inicio *</Label>
                  <Input
                    type="datetime-local"
                    value={formData.scheduled_start_time}
                    onChange={(e) => setFormData({ ...formData, scheduled_start_time: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Fecha y Hora de Fin (opcional)</Label>
                  <Input
                    type="datetime-local"
                    value={formData.scheduled_end_time}
                    onChange={(e) => setFormData({ ...formData, scheduled_end_time: e.target.value })}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Categoría</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="masterclass">Master Class</SelectItem>
                      <SelectItem value="webinar">Webinar</SelectItem>
                      <SelectItem value="conferencia">Conferencia</SelectItem>
                      <SelectItem value="tutorial">Tutorial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Privacidad</Label>
                  <Select value={formData.privacy_status} onValueChange={(v) => setFormData({ ...formData, privacy_status: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="public">Público</SelectItem>
                      <SelectItem value="unlisted">No listado</SelectItem>
                      <SelectItem value="private">Privado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Nivel de Acceso</Label>
                  <Select value={formData.access_level} onValueChange={(v) => setFormData({ ...formData, access_level: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="free">Gratuito</SelectItem>
                      <SelectItem value="affiliate">Afiliados</SelectItem>
                      <SelectItem value="vip">VIP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Speakers Selection */}
              <div>
                <Label>Ponentes (máximo 5)</Label>
                <div className="border rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                  {availableSpeakers.map((speaker) => (
                    <label key={speaker.id} className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded">
                      <input
                        type="checkbox"
                        checked={formData.speaker_ids.includes(speaker.id)}
                        onChange={(e) => {
                          if (e.target.checked && formData.speaker_ids.length < 5) {
                            setFormData({ ...formData, speaker_ids: [...formData.speaker_ids, speaker.id] });
                          } else if (!e.target.checked) {
                            setFormData({ ...formData, speaker_ids: formData.speaker_ids.filter(id => id !== speaker.id) });
                          }
                        }}
                        disabled={!formData.speaker_ids.includes(speaker.id) && formData.speaker_ids.length >= 5}
                        className="rounded"
                      />
                      <div>
                        <p className="font-medium text-sm">{speaker.name}</p>
                        <p className="text-xs text-gray-500">{speaker.email}</p>
                      </div>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">{formData.speaker_ids.length}/5 seleccionados</p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancelar
              </Button>
              <Button onClick={createLiveStream} disabled={creating} className="bg-red-600 hover:bg-red-700">
                {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Radio className="h-4 w-4 mr-2" />}
                Crear Transmisión
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminLives;
